import React, { lazy, Suspense, useState, useEffect, useMemo } from "react";
import FetchContent from "../dynamic/fetchContent.jsx";
import axios from "../../api/axios.js";
import useAuth from "../../hooks/useAuth.js";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import {
  contactDefaultDisplay,
  contactDefaultEntries,
  accommodationFormDefaultDisplay,
  accFormDefaultEntries,
} from "../../languages/defaultEntries.js";
import FetchCarouselContent from "../dynamic/fetchCarouselContent.jsx";
import Equipment from "./Equipment.jsx";
import Reviewsjson from "./Reviewsjson.jsx";
import ReviewsjsonEdit from "./ReviewsjsonEdit.jsx";
import Content from "../dynamic/Content.jsx";
import ImageContent from "../dynamic/ImageContent.jsx";
import CarouselContent from "./CarouselContent.js";
import Map from "../dynamic/Map";
import ReviewLinks from "./ReviewLinks.jsx";
import { useTranslation } from "react-i18next";
import { Certificates } from "./Certificates.jsx";
import { Link } from "react-router-dom";
import "../properties/propertiesLanding.css";

const WEBSITE_URL = "/dashboard/key";
const FetchImage = lazy(() => import("../dynamic/fetchImage.jsx"));
const WEBSITE_URL_CONTACT = "/contact";
const WEBSITE_CAL = "/websiteAuth/calendar";

 function buildPropertyUrl(domain) {
   const d = String(domain || "").trim();
   if (!d) return "";

   if (d.startsWith("http://") || d.startsWith("https://")) return d;

   const isLocal = d.includes("localhost") || d.includes("127.0.0.1") || d.includes(":");
   const protocol = isLocal ? "http" : "https";
   return `${protocol}://${d}`;
 }

export default function Home() {
  const { username } = useAuth();

   const axiosPrivate = useAxiosPrivate();

  const [accomodationType, setAccomodationType] = useState("");
  const [guests, setGuests] = useState(1);
  const [city, setCity] = useState("");
  const [nameAccommodation, setNameAccommodation] = useState("");
  const [meterSquared, setMeterSquared] = useState(0); // default value [m²]
  const [containsDefault, setContainsDefault] = useState(true);
  const [loadComplete, setLoadComplete] = useState(true);
  const [calendar, setCalendar] = useState([]);
  const [homePageVideoURL, setHomePageVideoURL] = useState("");

  const { auth } = useAuth();
  const { t } = useTranslation();

   const [properties, setProperties] = useState([]);
   const [propertiesLoading, setPropertiesLoading] = useState(false);
   const [propertiesError, setPropertiesError] = useState("");

   const isSubTenant = useMemo(() => {
     const host = String(window?.location?.hostname || "").trim().toLowerCase();
     const effective = (host && host !== "localhost" && host !== "127.0.0.1")
       ? host
       : String(username || "").trim().toLowerCase();

     const parts = effective.split(".").filter(Boolean);
     return parts.length > 2;
   }, [username]);

  const tenantKey = useMemo(() => {
    return String(username || "").trim().toLowerCase();
  }, [username]);

   useEffect(() => {
     let cancelled = false;

     const fetchProperties = async () => {
       if (isSubTenant) {
         setProperties([]);
         setPropertiesLoading(false);
         setPropertiesError("");
         return;
       }
       setPropertiesLoading(true);
       setPropertiesError("");
       try {
        const host = String(window?.location?.hostname || "").trim().toLowerCase();
        const mainDomain = (host && host !== "localhost" && host !== "127.0.0.1")
          ? host
          : String(username || "").trim().toLowerCase();
        const res = await axiosPrivate.get("/properties/public", { params: { mainDomain } });
         const list = Array.isArray(res?.data?.properties) ? res.data.properties : [];
         if (cancelled) return;
         setProperties(list);
       } catch (err) {
         if (cancelled) return;
         const msg =
           err?.response?.data?.message ||
           err?.message ||
           "Failed to load properties";
         setPropertiesError(msg);
       } finally {
         if (!cancelled) setPropertiesLoading(false);
       }
     };

     fetchProperties();

     return () => {
       cancelled = true;
     };
   }, [axiosPrivate, isSubTenant, username]);

   const propertyCards = useMemo(() => {
     return (properties || []).map((p) => {
       const domain = p?.domain;
       const name = p?.name || domain;
       return {
         id: p?.id || domain,
         name,
         domain,
         url: buildPropertyUrl(domain),
       };
     });
   }, [properties]);

   const openProperty = (url) => {
     if (!url) return;
     const u = new URL(url);
     window.location.href = `${u.origin}/login`;
   };

  const calendarUrl = useMemo(() => {
    return typeof calendar === "string" ? calendar.trim() : "";
  }, [calendar]);

  const showBookNow = useMemo(() => {
    const hasCalendar = Boolean(calendarUrl) && calendarUrl !== "no";
    if (!hasCalendar) return false;

    if (isSubTenant) return true;

    return (properties?.length || 0) === 0;
  }, [calendarUrl, isSubTenant, properties?.length]);

  const accDefaultDisplay = accommodationFormDefaultDisplay;
  const [propertyType, setPropertyType] = useState("");

  useEffect(() => {
    if (
      accomodationType !== accFormDefaultEntries.type &&
      guests !== accFormDefaultEntries.guests &&
      city !== contactDefaultEntries.city &&
      nameAccommodation !== accFormDefaultEntries.nameAccommodation
    ) {
      setContainsDefault(false);
    }
  }, [accomodationType, guests, city, nameAccommodation]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        await axios
          .get(WEBSITE_URL, {
            params: {
              username: username,
              key: "accomodationType", // specify the column you want to retrieve
            },
          })
          .then((res) => {
            setAccomodationType(res?.data?.accomodationType);
          });
        await axios
          .get(WEBSITE_URL, {
            params: {
              username: username,
              key: "guests", // specify the column you want to retrieve
            },
          })
          .then((res) => {
            setGuests(res?.data?.guests);
          });
        await axios
          .get(WEBSITE_URL, {
            params: {
              username: username,
              key: "nameAccommodation", // specify the column you want to retrieve
            },
          })
          .then((res) => {
            setNameAccommodation(res?.data?.nameAccommodation);
          });
        await axios
          .get(WEBSITE_URL, {
            params: {
              username: username,
              key: "homePageVideoURL", 
            },
          })
          .then((res) => {
            setHomePageVideoURL(res?.data?.homePageVideoURL);
          });
        await axios
          .get(WEBSITE_URL, {
            params: {
              username: username,
              key: "meterSquared", // specify the column you want to retrieve
            },
          })
          .then((res) => {
            setMeterSquared(res?.data?.meterSquared);
          });

           await axios.get(WEBSITE_CAL, {
                  params: { username: tenantKey },
                }).then((res) => {
                  setCalendar(res?.data?.calendar);
                }).catch((err) => {
                  console.log(err);
                });
        await axios
          .get(WEBSITE_URL_CONTACT, {
            params: {
              username: username,
              art: "nl",
              title: "contact",
            },
          })
          .then((res) => {
            console.log('hgdygsd',res.data.entry);
            setCity(res?.data?.entry?.city);
          });
      } catch (error) {
        console.error(error);
      }
    };
    fetchContent();
  }, [username, tenantKey]);

  useEffect(() => {
    setLoadComplete(
      !!accomodationType && !!guests && !!nameAccommodation && !!city
    );
  }, [loadComplete, accomodationType, guests, nameAccommodation, city]);
  // display empty string if db contains default entry
  useEffect(() => {
    setPropertyType(
      accomodationType === accFormDefaultEntries.type || !loadComplete
        ? ""
        : t("codes.acc_types." + accomodationType)
    );
  }, [accomodationType, t, loadComplete]);

  function extractYouTubeId(url) {
  const regExp =
    /^.*(?:youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[1].length === 11 ? match[1] : null;
}

const allowedDomains = [
  "hausclarysse.com",
  "activemontgoflat.com",
  "studiogocki.com",
  "thecityghent.com"
];

const currentDomain = window.location.hostname;
const showMoreButton = allowedDomains.includes(currentDomain);

  return (
    <div className="default">
      <div className="image-content home-hero">
        <Suspense fallback={<h1>Still Loading…</h1>}>
          {!auth?.role ? (
            <FetchImage title="home" type="image-home" />
          ) : (
            <ImageContent title="home" username={username} type="image-home" />
          )}
          <div className="hero-conent">
            <h2>
              {nameAccommodation === accFormDefaultEntries.nameAccommodation
                ? accDefaultDisplay.nameAccommodation
                : nameAccommodation}
            </h2>
            {!auth?.role ? (
              <p style={{ textAlign: "justify" }}>
                <FetchContent title="accommodation-about" />
              </p>
            ) : (
              <p>
                <Content title={"accommodation-about"} styling="p" />
              </p>
            )}

            {showBookNow && (
              <Link className="book-now-btn" to={calendarUrl}>
                Book Now
              </Link>
            )}
            {showMoreButton && (
              <a
                href="https://claryssesignaturestays.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="book-now-btn"
                style={{ marginTop: "1rem" }}
              >
                We have more accommodations →
              </a>
            )}
          </div>
        </Suspense>
      </div>

      {homePageVideoURL && (
        <div className="video-section stripe container">
          <div
            className="video-wrapper"
            style={{
              position: "relative",
              paddingTop: "56.25%",
              marginTop: "2rem",
            }}
          >
            <iframe
              src={`https://www.youtube.com/embed/${extractYouTubeId(
                homePageVideoURL
              )}?autoplay=1&mute=1&loop=1&playlist=${extractYouTubeId(
                homePageVideoURL
              )}&controls=0&rel=0&modestbranding=1`}
              title="Intro Video"
              allow="autoplay; encrypted-media"
              allowFullScreen
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                borderRadius: "16px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              }}
            ></iframe>
          </div>
        </div>
      )}

      {loadComplete && (
        <div>
          {containsDefault && Boolean(auth?.role) && (
            <p className="hint-msg">{t("hints.contains_default")}</p>
          )}

          <div className="stripe container about-us-main">
            <div className="about-us-main-img">
              {!auth?.role ? (
                <FetchImage
                  title="home-about"
                  type="about-us-main-img-container"
                />
              ) : (
                <ImageContent
                  title="home-about"
                  username={username}
                  type="about-us-main-img-container"
                />
              )}
            </div>

            <div className="about-us-main-content">
              <div className="heading-brannew">
                {" "}
                {nameAccommodation === accFormDefaultEntries.nameAccommodation
                  ? accDefaultDisplay.nameAccommodation
                  : nameAccommodation}{" "}
              </div>

              <div className="subheading-brannew">
                {" "}
                {propertyType} - {t("home_page.upto")}
                {guests === accFormDefaultEntries.guests
                  ? accDefaultDisplay.guests
                  : guests}{" "}
                {t("home_page.guests")} - {t("home_page.in")}{" "}
                {city === contactDefaultEntries.city
                  ? contactDefaultDisplay.city
                  : city}{" "}
                ({meterSquared} m²)
              </div>

              {!auth?.role ? (
                <div className="p-brandnew">
                  <FetchContent title="p-accommodation" />
                </div>
              ) : (
                <div className="p-brandnew">
                  <Content title={"p-accommodation"} styling="p" />
                </div>
              )}
            </div>
          </div>

          {/* <div className="stripe container ws-pre-wrap">
            <div className="p">
              <h1>
                {" "}
                {nameAccommodation === accFormDefaultEntries.nameAccommodation
                  ? accDefaultDisplay.nameAccommodation
                  : nameAccommodation}{" "}
              </h1>
              <h2>
                {" "}
                {propertyType} - {t("home_page.upto")}
                {guests === accFormDefaultEntries.guests
                  ? accDefaultDisplay.guests
                  : guests}{" "}
                {t("home_page.guests")} - {t("home_page.in")}{" "}
                {city === contactDefaultEntries.city
                  ? contactDefaultDisplay.city
                  : city}{" "}
                ({meterSquared} m²)
              </h2>
              <hr></hr>
            </div>
            {!auth?.role ? (
              <div className="p">
                <FetchContent title="p-accommodation" />
              </div>
            ) : (
              <div className="p">
                <Content title={"p-accommodation"} styling="p" />
              </div>
            )}
          </div> */}
          <div className="stripe stripe-carousel">
            <Suspense fallback={<h1>Still Loading…</h1>}>
              {!auth?.role ? (
                <FetchCarouselContent title="home" username={username} />
              ) : (
                <CarouselContent
                  title="home"
                  username={username}
                  maxImages={15}
                />
              )}
            </Suspense>
          </div>

          {!isSubTenant && (
            <div className="stripe properties-landing">
              <div className="container properties-landing__container">
                <h2 className="properties-landing__title">Our Destinations</h2>

                {propertiesLoading && (
                  <div className="properties-landing__status">Loading…</div>
                )}

                {!propertiesLoading && propertiesError && (
                  <div className="properties-landing__status properties-landing__status--error">
                    {propertiesError}
                  </div>
                )}

                {!propertiesLoading && !propertiesError && (
                  <div className="properties-landing__grid">
                    {propertyCards.map((c) => (
                      <div
                        key={c.id}
                        className="properties-landing__card"
                        onClick={() => openProperty(c.url)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") openProperty(c.url);
                        }}
                      >
                        <div className="properties-landing__image" />

                        <div className="properties-landing__content">
                          <h3 className="properties-landing__cardTitle">{c.name}</h3>
                          <p className="properties-landing__cardSubtitle">{c.domain}</p>
                        </div>

                        <button
                          type="button"
                          className="properties-landing__button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openProperty(c.url);
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="stripe">
            <div className="container new-container-padding">
              <h2> {t("home_page.equipment")} </h2>
              <Equipment />
            </div>
          </div>

          {/* <div className="stripe-services">
            <Services />
          </div> */}

          <div>{!auth?.role ? <Map /> : <Map adminUser={true} />}</div>

          <div className=" stripe">
            <div className="container text-center">
              {!auth?.role ? <Reviewsjson /> : <ReviewsjsonEdit />}
            </div>
          </div>
          <div className=" stripe">
            <div className="container text-center">
              <Certificates />
            </div>
          </div>
          <div className="stripe">
            <ReviewLinks />
          </div>
        </div>
      )}
    </div>
  );
}
